import { isValidIssueType } from '../../components/entities/IssueType'
import { IssueType, SourceType, TransactionType } from '@finance-lab/utils/types'

const validLinkedSourceTypes: SourceType[] = [
    'Publication',
    'Terms',
    'New commitments',
    'Update',
    'Outcome',
    'Stabilisation',
    'Cancellation',
    'Conversion',
    'Offset',
    'Repayment',

    // Mentioned earlier, but not used:
    // 'Updated terms',
    // 'Updated schedule',
]
const validUploadedSourceTypes: SourceType[] = ['Prospectus', 'Memorandum', 'Supplement', 'InformationDocument']
// These source types are singular, ie there can only be one of each type
export const singularIssueSourceTypes: SourceType[] = [
    'Publication',
    'Terms',
    'Cancellation',
    'Outcome',
    'Memorandum',
    'Prospectus',
]
export const singularWarrantSourceTypes: SourceType[] = ['Publication', 'Cancellation', 'Memorandum', 'Prospectus']
export const singularConvertibleSourceTypes: SourceType[] = ['Publication']

export const isSingularIssueSourceType = (type: SourceType) => singularIssueSourceTypes.includes(type)
export const isSingularWarrantSourceType = (type: SourceType) => singularWarrantSourceTypes.includes(type)

export const getValidLinkedSourceTypes = () => validLinkedSourceTypes
export const getValidUploadedSourceTypes = () => validUploadedSourceTypes

/**
 * Retrieves a list of valid source types based on the provided issue type.
 * If no issue type is specified, returns all valid linked and uploaded source types.
 * Throws an error if the provided issue type is invalid.
 *
 * @param {string|null} issueType Optional parameter specifying the type of issue.
 * @returns {string[]} An array of valid source types corresponding to the specified issue type.
 * @throws {Error} If the issue type is not valid.
 */
export const getValidSourceTypes = (
    transactionType: TransactionType | null = null,
    issueType: IssueType | null = null,
): SourceType[] => {
    if (!transactionType || (transactionType === 'Issue' && !issueType))
        return [...validLinkedSourceTypes, ...validUploadedSourceTypes]
    if (transactionType !== 'Warrant' && transactionType !== 'Convertible' && !isValidIssueType(issueType))
        throw new Error('Invalid transactionType: ' + transactionType)
    if (issueType === 'ABB Directed issue') return ['Cancellation', 'Outcome', 'Publication']
    if (issueType === 'Directed issue') return ['Cancellation', 'Update', 'Outcome', 'Prospectus', 'Publication']
    if (issueType === 'Guarantor fee issue') return ['Publication']

    if (issueType === 'IPO')
        return [
            'Cancellation',
            'Update',
            'Stabilisation',
            'Memorandum',
            'Outcome',
            'Prospectus',
            'Publication',
            'Supplement',
        ]
    if (issueType === 'Non-cash issue') return ['Cancellation', 'Update', 'Outcome', 'Prospectus', 'Publication']
    // if (issueType === 'Other') return ['Publication']

    if (issueType === 'Rights issue')
        return [
            'Cancellation',
            'Update',
            'Memorandum',
            'New commitments',
            'Outcome',
            'Prospectus',
            'Publication',
            'Supplement',
            'Terms',
            'InformationDocument',
        ]
    if (transactionType === 'Warrant') return ['Update', 'New commitments', 'Outcome', 'Publication', 'Terms']
    if (transactionType === 'Convertible') return ['Publication', 'Update', 'Conversion', 'Offset', 'Repayment']

    throw new Error('Unknown issueType: ' + (issueType as string))
}
const isValidLinkedSourceType = (type: SourceType) => validLinkedSourceTypes.includes(type)
const isValidUploadedSourceType = (type: SourceType) => validUploadedSourceTypes.includes(type)
export const isValidSourceType = (sourceType: SourceType, issueType: IssueType | null = null) =>
    issueType
        ? getValidSourceTypes('Issue', issueType).includes(sourceType)
        : isValidLinkedSourceType(sourceType) || isValidUploadedSourceType(sourceType)
